import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';

interface ResourceAnalyticsProps {
  projectId: string;
}

interface OverallAnalytics {
  total_hours: number;
  total_cost: number;
  resource_count: number;
  resource_counts_by_type: Record<string, number>;
  average_utilization: number;
  assignment_count: number;
}

interface SCurveData {
  period: string;
  hours: number;
  cost: number;
  cumulative_hours: number;
  cumulative_cost: number;
}

interface HistogramData {
  period: string;
  hours: number;
  resource_count: number;
}

interface UtilizationAnalysis {
  resources: Array<{
    resource_id: string;
    resource_name: string;
    resource_type: string;
    max_units: number;
    utilization: number;
    status: string;
  }>;
  over_allocated: Array<{
    resource_id: string;
    resource_name: string;
    utilization: number;
  }>;
  under_allocated: Array<{
    resource_id: string;
    resource_name: string;
    utilization: number;
  }>;
  statistics: {
    average_utilization: number;
    max_utilization: number;
    min_utilization: number;
    total_resources: number;
    over_allocated_count: number;
    under_allocated_count: number;
  };
}

interface CostAnalysis {
  total_cost: number;
  costs_by_type: Array<{
    type: string;
    cost: number;
  }>;
  costs_by_resource: Array<{
    resource_name: string;
    resource_type: string;
    total_cost: number;
    total_hours: number;
  }>;
  assignment_count: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const TIME_PERIOD_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom Range' },
];

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  'RT_Mat': 'Material',
  'RT_Equip': 'Equipment',
  'RT_Labor': 'Labor',
  'RT_NonLabor': 'Non-Labor',
  'MATERIAL': 'Material',
  'EQUIPMENT': 'Equipment',
  'LABOR': 'Labor',
  'NONLABOR': 'Non-Labor',
};

function getResourceTypeLabel(type: string) {
  return RESOURCE_TYPE_LABELS[type] || type || 'Unknown';
}

const ResourceAnalytics: React.FC<ResourceAnalyticsProps> = ({ projectId }) => {
  const [timePeriod, setTimePeriod] = useState('week');
  const [resourceType, setResourceType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [overallAnalytics, setOverallAnalytics] = useState<OverallAnalytics | null>(null);
  const [sCurveData, setSCurveData] = useState<SCurveData[]>([]);
  const [histogramData, setHistogramData] = useState<HistogramData[]>([]);
  const [utilizationAnalysis, setUtilizationAnalysis] = useState<UtilizationAnalysis | null>(null);
  const [costAnalysis, setCostAnalysis] = useState<CostAnalysis | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overallRes, sCurveRes, histogramRes, utilizationRes, costRes] = await Promise.all([
        fetch(`/api/projects/${projectId}/resources/analytics/overall`),
        fetch(`/api/projects/${projectId}/resources/analytics/s-curve?time_period=${timePeriod}`),
        fetch(`/api/projects/${projectId}/resources/analytics/histogram?time_period=${timePeriod}${resourceType ? `&resource_type=${resourceType}` : ''}`),
        fetch(`/api/projects/${projectId}/resources/analytics/utilization`),
        fetch(`/api/projects/${projectId}/resources/analytics/cost-analysis`)
      ]);

      if (!overallRes.ok || !sCurveRes.ok || !histogramRes.ok || !utilizationRes.ok || !costRes.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const [overallData, sCurveDataRes, histogramDataRes, utilizationData, costData] = await Promise.all([
        overallRes.json(),
        sCurveRes.json(),
        histogramRes.json(),
        utilizationRes.json(),
        costRes.json()
      ]);

      setOverallAnalytics(overallData.data);
      setSCurveData(sCurveDataRes.data);
      setHistogramData(histogramDataRes.data);
      setUtilizationAnalysis(utilizationData.data);
      setCostAnalysis(costData.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, [projectId, timePeriod, resourceType]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const _getStatusColor = (status: string) => {
    switch (status) {
      case 'over_allocated': return 'error';
      case 'under_allocated': return 'warning';
      case 'optimal': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, alignItems: 'center' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select
                value={timePeriod}
                label="Time Period"
                onChange={(e) => setTimePeriod(e.target.value)}
              >
                {TIME_PERIOD_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Resource Type</InputLabel>
              <Select
                value={resourceType}
                label="Resource Type"
                onChange={(e) => setResourceType(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="LABOR">Labor</MenuItem>
                <MenuItem value="EQUIPMENT">Equipment</MenuItem>
                <MenuItem value="MATERIAL">Material</MenuItem>
                <MenuItem value="NONLABOR">Non-Labor</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Overall Analytics */}
      {overallAnalytics && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Hours
              </Typography>
              <Typography variant="h4">
                {overallAnalytics.total_hours.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Cost
              </Typography>
              <Typography variant="h4">
                ${overallAnalytics.total_cost.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Resources
              </Typography>
              <Typography variant="h4">
                {overallAnalytics.resource_count}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Utilization
              </Typography>
              <Typography variant="h4">
                {overallAnalytics.average_utilization.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* S-Curve Chart */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cumulative Hours (S-Curve)
            </Typography>
            {!sCurveData.length && (
              <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
                No S-curve data available for the selected period.
              </Typography>
            )}
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={sCurveData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="cumulative_hours" stroke="#8884d8" fill="#8884d8" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cumulative Cost (S-Curve)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={sCurveData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="cumulative_cost" stroke="#82ca9d" fill="#82ca9d" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Histogram */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Resource Hours Distribution
          </Typography>
          {!histogramData.length && (
            <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
              No resource hours distribution data available for the selected period.
            </Typography>
          )}
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={histogramData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="hours" fill="#8884d8" name="Hours" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Utilization Analysis */}
      {utilizationAnalysis && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resource Utilization
              </Typography>
              {utilizationAnalysis.resources.length === 0 && (
                <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
                  No resource utilization data available for this project.
                </Typography>
              )}
              {utilizationAnalysis.resources.length > 0 && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Average: {utilizationAnalysis.statistics.average_utilization.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Max: {utilizationAnalysis.statistics.max_utilization.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Min: {utilizationAnalysis.statistics.min_utilization.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Over-allocated Resources ({utilizationAnalysis.over_allocated.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {utilizationAnalysis.over_allocated.slice(0, 5).map((resource) => (
                      <Chip
                        key={resource.resource_id}
                        label={`${resource.resource_name} (${resource.utilization.toFixed(0)}%)`}
                        color="error"
                        size="small"
                      />
                    ))}
                  </Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Under-allocated Resources ({utilizationAnalysis.under_allocated.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {utilizationAnalysis.under_allocated.slice(0, 5).map((resource) => (
                      <Chip
                        key={resource.resource_id}
                        label={`${resource.resource_name} (${resource.utilization.toFixed(0)}%)`}
                        color="warning"
                        size="small"
                      />
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Utilization Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Over-allocated', value: utilizationAnalysis.statistics.over_allocated_count },
                      { name: 'Optimal', value: utilizationAnalysis.statistics.total_resources - utilizationAnalysis.statistics.over_allocated_count - utilizationAnalysis.statistics.under_allocated_count },
                      { name: 'Under-allocated', value: utilizationAnalysis.statistics.under_allocated_count }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Cost Analysis */}
      {costAnalysis && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost by Resource Type
              </Typography>
              {costAnalysis.costs_by_type.length === 0 && (
                <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
                  No cost data available for this project.
                </Typography>
              )}
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={costAnalysis.costs_by_type}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ type, cost }) => `${getResourceTypeLabel(type)}: $${cost.toLocaleString()}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="cost"
                  >
                    {costAnalysis.costs_by_type.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Resources by Cost
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {costAnalysis.costs_by_resource
                  .sort((a, b) => b.total_cost - a.total_cost)
                  .slice(0, 10)
                  .map((resource, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        {resource.resource_name}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ${resource.total_cost.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default ResourceAnalytics; 